import {
  LESSON_THEME_COLORS,
  defaultThemeTokens,
  DEFAULT_ASSISTANT_SETTINGS,
  normalizeAssistantSettings,
} from '@course-authoring/shared-types';
import { BASE_FONT_OPTIONS, resolveBuiltInFontFamily } from '../../theme/fontOptions';
import type { AccountThemeDto, SaveAccountThemePayload } from '../../api/accountThemeApi';

export type ThemePayload = Omit<SaveAccountThemePayload, 'externalAccountId'>;

export interface ThemeDirtyFields {
  logo: boolean;
  primaryLogo: boolean;
  certificateLogo: boolean;
  separateCertificateLogo: boolean;
  font: boolean;
  colors: Record<string, boolean>;
  assistant: boolean;
  assistantName: boolean;
  assistantGreeting: boolean;
  assistantIntro: boolean;
  assistantAvatar: boolean;
}

export interface ThemeDraftComparison {
  dirtyFields: ThemeDirtyFields;
  isDirty: boolean;
}

export function getThemeDraftComparison(
  current: ThemePayload,
  baseline: ThemePayload | null,
  stagedFiles: { primaryLogo: boolean; certificateLogo: boolean; customFont: boolean; assistantAvatar?: boolean },
  certificateLogoEnabled: boolean,
  baselineCertificateLogoEnabled: boolean,
): ThemeDraftComparison {
  const colors = getColorDirtyFields(currentColors(current), baseline);
  const primaryLogo = stagedFiles.primaryLogo
    || current.primaryLogoAssetId !== (baseline?.primaryLogoAssetId ?? null);
  const certificateLogo = stagedFiles.certificateLogo
    || current.certificateLogoAssetId !== (baseline?.certificateLogoAssetId ?? null);
  const separateCertificateLogo = certificateLogoEnabled !== baselineCertificateLogoEnabled;
  const font = stagedFiles.customFont
    || current.fontFamily !== (baseline?.fontFamily ?? BASE_FONT_OPTIONS[0]!.family)
    || current.customFontAssetId !== (baseline?.customFontAssetId ?? null);
  const currentAssistant = normalizeAssistantSettings(current.assistant ?? DEFAULT_ASSISTANT_SETTINGS);
  const baselineAssistant = normalizeAssistantSettings(baseline?.assistant ?? DEFAULT_ASSISTANT_SETTINGS);
  const assistantAvatar = Boolean(stagedFiles.assistantAvatar)
    || current.assistantAvatarAssetId !== (baseline?.assistantAvatarAssetId ?? null);
  const assistantName = currentAssistant.name !== baselineAssistant.name;
  const assistantGreeting = currentAssistant.greeting !== baselineAssistant.greeting;
  const assistantIntro = currentAssistant.introMode !== baselineAssistant.introMode
    || currentAssistant.intro !== baselineAssistant.intro;
  const dirtyFields: ThemeDirtyFields = {
    logo: primaryLogo || certificateLogo || separateCertificateLogo,
    primaryLogo,
    certificateLogo,
    separateCertificateLogo,
    font,
    colors,
    assistant: assistantAvatar || assistantName || assistantGreeting || assistantIntro,
    assistantAvatar,
    assistantName,
    assistantGreeting,
    assistantIntro,
  };

  return {
    dirtyFields,
    isDirty: dirtyFields.logo || dirtyFields.font || dirtyFields.assistant || Object.values(colors).some(Boolean),
  };
}

function currentColors(payload: ThemePayload): Record<string, string> {
  return {
    [COLOR_KEY.sidebar]: payload.themeColors.sidebar,
    [COLOR_KEY.sidebarLogo]: payload.themeColors.sidebarLogo,
    [COLOR_KEY.accent]: payload.themeColors.accent,
    [COLOR_KEY.lesson1]: payload.lessonColors.color1,
    [COLOR_KEY.lesson2]: payload.lessonColors.color2,
    [COLOR_KEY.lesson3]: payload.lessonColors.color3,
    [COLOR_KEY.lesson4]: payload.lessonColors.color4,
  };
}

// ---------------------------------------------------------------------------
// Logo constants
// ---------------------------------------------------------------------------

export const LOGO_GUIDELINES: string[] = [
  'The file must be a PNG, JPG or GIF no larger than 5MB.',
  'We recommend a rectangular image approximately 70px tall by 150px wide.',
  'If you upload a larger image, it will be sized down to fit in the space.',
  'If you upload a smaller image, it will be sized up, which could result in a deterioration in image quality.',
];

export const LOGO_ACCEPT = 'image/png,image/jpeg,image/gif';

// ---------------------------------------------------------------------------
// Font constants
// ---------------------------------------------------------------------------

export const FONT_ACCEPT = '.woff,.woff2,.ttf,.otf';
export const FONT_ALLOWED_EXTENSIONS = new Set(['woff', 'woff2', 'ttf', 'otf']);

// ---------------------------------------------------------------------------
// Color keys, specs, and DTO mappers
// ---------------------------------------------------------------------------

/**
 * Stable spec-ids for each editable color row. Shared by the row specs and the
 * DTO ↔ state mappers so a mistyped key is a compile error, not a dropped color.
 */
export const COLOR_KEY = {
  sidebar: 'left-panel-background',
  sidebarLogo: 'logo-background',
  accent: 'accent-color',
  lesson1: 'lesson-color-1',
  lesson2: 'lesson-color-2',
  lesson3: 'lesson-color-3',
  lesson4: 'lesson-color-4',
} as const;

export function getColorDirtyFields(
  colors: Record<string, string>,
  baseline: {
    themeColors: { sidebar: string; sidebarLogo: string; accent: string };
    lessonColors: { color1: string; color2: string; color3: string; color4: string };
  } | null,
): Record<string, boolean> {
  const saved = baseline;
  return {
    [COLOR_KEY.sidebar]: saved ? colors[COLOR_KEY.sidebar] !== saved.themeColors.sidebar : false,
    [COLOR_KEY.sidebarLogo]: saved ? colors[COLOR_KEY.sidebarLogo] !== saved.themeColors.sidebarLogo : false,
    [COLOR_KEY.accent]: saved ? colors[COLOR_KEY.accent] !== saved.themeColors.accent : false,
    [COLOR_KEY.lesson1]: saved ? colors[COLOR_KEY.lesson1] !== saved.lessonColors.color1 : false,
    [COLOR_KEY.lesson2]: saved ? colors[COLOR_KEY.lesson2] !== saved.lessonColors.color2 : false,
    [COLOR_KEY.lesson3]: saved ? colors[COLOR_KEY.lesson3] !== saved.lessonColors.color3 : false,
    [COLOR_KEY.lesson4]: saved ? colors[COLOR_KEY.lesson4] !== saved.lessonColors.color4 : false,
  };
}

export interface ColorRowSpec {
  id: string;
  label: string;
  detail?: string;
  defaultValue: string;
}

/**
 * Overlays stored DTO values on top of the code-floor defaults.
 * Returns the effective color map (keyed by spec-id) and built-in font family.
 */
export function toEffective(
  dto: AccountThemeDto | undefined,
  themeFloor: { sidebar: string; sidebarLogo: string; accent: string },
): { colors: Record<string, string>; builtInFontFamily: string } {
  const defaultFont = BASE_FONT_OPTIONS[0]!.family;

  const tc = dto?.themeColors;
  const lc = dto?.lessonColors;

  const colors: Record<string, string> = {
    [COLOR_KEY.sidebar]: tc?.sidebar ?? themeFloor.sidebar,
    [COLOR_KEY.sidebarLogo]: tc?.sidebarLogo ?? themeFloor.sidebarLogo,
    [COLOR_KEY.accent]: tc?.accent ?? themeFloor.accent,
    [COLOR_KEY.lesson1]: lc?.color1 ?? LESSON_THEME_COLORS.color1,
    [COLOR_KEY.lesson2]: lc?.color2 ?? LESSON_THEME_COLORS.color2,
    [COLOR_KEY.lesson3]: lc?.color3 ?? LESSON_THEME_COLORS.color3,
    [COLOR_KEY.lesson4]: lc?.color4 ?? LESSON_THEME_COLORS.color4,
  };

  // If it's the custom sentinel or unknown, default to the first built-in
  const builtInFontFamily = resolveBuiltInFontFamily(dto?.fontFamily ?? null, defaultFont);

  return { colors, builtInFontFamily };
}

/** Maps spec-id color keys back to the API payload shape. */
export function toPayload(
  colors: Record<string, string>,
  fontFamily: string,
  primaryLogoAssetId: number | null,
  certificateLogoAssetId: number | null,
  customFontAssetId: number | null,
): {
  themeColors: { sidebar: string; sidebarLogo: string; accent: string };
  lessonColors: { color1: string; color2: string; color3: string; color4: string };
  fontFamily: string;
  primaryLogoAssetId: number | null;
  certificateLogoAssetId: number | null;
  customFontAssetId: number | null;
} {
  return {
    themeColors: {
      sidebar: colors[COLOR_KEY.sidebar] ?? defaultThemeTokens.backgrounds.sidebar,
      sidebarLogo: colors[COLOR_KEY.sidebarLogo] ?? defaultThemeTokens.backgrounds.sidebarLogo,
      accent: colors[COLOR_KEY.accent] ?? defaultThemeTokens.brand.accent ?? defaultThemeTokens.brand.primary,
    },
    lessonColors: {
      color1: colors[COLOR_KEY.lesson1] ?? LESSON_THEME_COLORS.color1,
      color2: colors[COLOR_KEY.lesson2] ?? LESSON_THEME_COLORS.color2,
      color3: colors[COLOR_KEY.lesson3] ?? LESSON_THEME_COLORS.color3,
      color4: colors[COLOR_KEY.lesson4] ?? LESSON_THEME_COLORS.color4,
    },
    fontFamily,
    primaryLogoAssetId,
    certificateLogoAssetId,
    customFontAssetId,
  };
}
